import React from "react";
import * as Label from "@radix-ui/react-label";
import { cmsColSpanClass } from "@/components/organisms/CmsForm/cmsColSpan";
import type { CmsFieldBaseProps } from "./CmsFieldBase";
import "./CmsField.scss";

interface CmsFieldShellProps extends CmsFieldBaseProps {
  children?: React.ReactNode;
  labelFor?: string;
  labelId?: string;
}

/**
 * Shared CMS field layout: label, hint/error, and control slot.
 * Internal building block for all Cms* field molecules.
 */
export function CmsFieldShell({
  label,
  id,
  hint,
  error,
  requiredMark,
  children,
  className,
  colSpan,
  labelFor,
  labelId,
}: CmsFieldShellProps) {
  const errorId = error && id ? `${id}-error` : undefined;
  const labelTarget = labelFor ?? id;

  const requiredMarkNode =
    requiredMark && label ? (
      <span className="cms-field__required" aria-hidden="true">
        {" "}
        *
      </span>
    ) : null;

  return (
    <div
      className={["cms-field", cmsColSpanClass(colSpan), className]
        .filter(Boolean)
        .join(" ")}
    >
      {label && labelTarget ? (
        <Label.Root htmlFor={labelTarget} className="cms-field__label">
          {label}
          {requiredMarkNode}
        </Label.Root>
      ) : label && labelId ? (
        <Label.Root id={labelId} className="cms-field__label">
          {label}
          {requiredMarkNode}
        </Label.Root>
      ) : label ? (
        <span className="cms-field__label">
          {label}
          {requiredMarkNode}
        </span>
      ) : null}
      <span
        id={errorId}
        className={error ? "cms-field__error" : "cms-field__hint"}
        role={error ? "alert" : undefined}
      >
        {error ?? hint}
      </span>
      {children != null && (
        <div className="cms-field__control">{children}</div>
      )}
    </div>
  );
}
